import {
  interleaveThinkingAndText,
  toolCallIsContextFull,
  type TimelineTextInsertion,
} from '@/components/case-partner/components/assistantTurnTimeline';
import type { ThinkingStep, ToolCall } from '@/hooks/useChat';

const ACTIVITY_TAIL = /^(searching|planning|browsing|reading|analysing|analyzing|synthesising|synthesizing|finding)\b/i;

// "Deep dive" and the older "Consulting…" forms are still on persisted turns,
// so they stay parseable even though nothing writes them any more.
const OBJECTIVE_PREFIXES = [
  /^Deep dive\s*[-–—:]\s*/i,
  /^Consulting (?:expert|research) agent:\s*/i,
  /^Consulted (?:expert|research) agent:\s*/i,
  /^(?:Expert|Research) agent:\s*/i,
  /^Researching:\s*/i,
];

const AGENT_TITLE_SUFFIX = 'agent';
const AGENT_TITLE_SUFFIX_RE = /\s+agent$/i;
const AGENT_WORKING_STATUS = 'working';
const WEB_RESEARCH_TOOL_NAME = 'spawn_web_research';

export const isWebResearchTool = (name?: string | null): boolean =>
  name === WEB_RESEARCH_TOOL_NAME;

export const specialistKindLabel = (name?: string | null): string =>
  isWebResearchTool(name) ? 'Web Agent' : 'Agent';

const specialistKindPlural = (name?: string | null): string =>
  isWebResearchTool(name) ? 'Web Agents' : 'agents';

export const agentTitleForTopic = (topic: string): string =>
  topic ? `${topic} ${AGENT_TITLE_SUFFIX}` : '';

// The label names what the agent is expert in, so it is shown as written. It
// used to be trimmed back to a bare name, dropping a trailing "stance" or
// "position", which read as a person handed a job rather than an agent with a
// standing brief — and would now cut the subject out of a good label, since a
// party's position on an issue is the very thing such an agent knows about.
export const researchTopicForDisplay = (topic: string): string => {
  const trimmed = topic.trim().replace(/\s+/g, ' ');
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toLocaleUpperCase() + trimmed.slice(1);
};

const parseJsonObject = (value?: unknown): Record<string, unknown> | null => {
  if (!value) return null;
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : null;
  } catch {
    return null;
  }
};

export const toolCallFailed = (
  tool: Pick<ToolCall, 'function' | 'status'> | undefined,
): boolean => {
  if (!tool || toolCallIsContextFull(tool)) return false;
  if (tool.status === 'failed') return true;
  const output = parseJsonObject(tool.function?.output);
  return output?.success === false
    || (
      typeof output?.error === 'string'
      && output.error.trim().length > 0
      && output.success !== true
    );
};

export const clipResearchAgentText = (text: string, max = 80): string => {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 3).trimEnd()}...`;
};

export const shortResearchTopic = (objective: string, max = 36): string => {
  const text = objective.trim().replace(/\s+/g, ' ');
  if (!text) return '';
  if (text.length <= max) return text;
  const clipped = text.slice(0, max - 1).replace(/\s+\S*$/, '');
  return `${clipped || text.slice(0, max - 1)}…`;
};

export const getResearchAgentObjective = (
  tool: Pick<ToolCall, 'function' | 'displayInfo' | 'objective'>,
): string => {
  if (typeof tool.objective === 'string' && tool.objective.trim()) {
    return tool.objective.trim();
  }
  const args = parseJsonObject(tool.function?.arguments);
  const output = parseJsonObject(tool.function?.output);
  const fromArgs = typeof args?.objective === 'string' ? args.objective.trim() : '';
  const fromOutput = typeof output?.objective === 'string' ? output.objective.trim() : '';
  if (fromArgs) return fromArgs;
  if (fromOutput) return fromOutput;

  const info = (tool.displayInfo || '').trim();
  const suffixed = info.replace(AGENT_TITLE_SUFFIX_RE, '').trim();
  if (suffixed && suffixed !== info && !ACTIVITY_TAIL.test(suffixed)) return suffixed;
  for (const prefix of OBJECTIVE_PREFIXES) {
    if (!prefix.test(info)) continue;
    const rest = info.replace(prefix, '').trim();
    if (rest && !ACTIVITY_TAIL.test(rest) && !rest.includes(',')) return rest;
  }
  return '';
};

export const getResearchAgentTopic = (
  tool: Pick<ToolCall, 'function' | 'displayInfo' | 'objective'>,
): string => {
  const args = parseJsonObject(tool.function?.arguments);
  const output = parseJsonObject(tool.function?.output);
  const fromArgs = typeof args?.display_label === 'string' ? args.display_label.trim() : '';
  const fromOutput = typeof output?.display_label === 'string' ? output.display_label.trim() : '';
  return researchTopicForDisplay(
    fromArgs || fromOutput || shortResearchTopic(getResearchAgentObjective(tool)),
  );
};

export type ResearchAgentOutcome = {
  tone: 'completed' | 'stopped' | 'failed';
  text: string;
};

const researchOutputIsRunning = (
  output: Record<string, unknown> | null,
): boolean => output?.status === 'running';

/**
 * How an agent's run ended, once it has ended. Null while it is still running.
 *
 * The tone is carried separately from the text so the renderer picks the icon;
 * a caller cannot accidentally put a tick next to a run that did not finish.
 * A run cut short by a user stop is the one ending that reports no output at
 * all, so the status has to be read alongside it.
 */
export const getResearchAgentOutcome = (
  tool: Pick<ToolCall, 'function' | 'status'>,
): ResearchAgentOutcome | null => {
  const output = parseJsonObject(tool.function?.output);
  if (toolCallFailed(tool)) {
    return { tone: 'failed', text: 'Could not complete' };
  }
  if (tool.status === 'stopped' || output?.stopped) {
    return { tone: 'stopped', text: 'Stopped' };
  }
  if (!output || researchOutputIsRunning(output)) return null;
  return { tone: 'completed', text: 'Completed' };
};

const lastActivitySegment = (value: string): string | undefined => {
  const parts = value.split(',').map((part) => part.trim()).filter(Boolean);
  const current = parts[parts.length - 1];
  if (current && ACTIVITY_TAIL.test(current)) return current;
  return undefined;
};

export const liveResearchAgentDisplay = (displayInfo?: string): string => {
  if (!displayInfo) return `Agent ${AGENT_WORKING_STATUS}...`;
  return displayInfo
    .replace(/^Researching:\s*/i, 'Agent - ')
    .replace(/^Consulting (?:expert|research) agent:\s*/i, 'Agent - ')
    .replace(/^(?:Expert agent|Research agent|Research):\s*/i, 'Agent - ')
    .replace(/^Deep dive\s*[-–—:]\s*/i, 'Agent - ');
};

export const currentResearchAgentStep = (displayInfo?: string): string | undefined => {
  const info = (displayInfo || '').trim();
  if (!info) return undefined;
  const live = liveResearchAgentDisplay(info);
  if (/^Agent(?: working)?(?:\.\.\.|…)?$/i.test(live)) {
    return undefined;
  }
  const prefixed = live.match(/^Agent\s*(?::|-|–|—)\s*(.*)$/i);
  const rest = (prefixed ? prefixed[1] : live).trim();
  return lastActivitySegment(rest) ?? (ACTIVITY_TAIL.test(rest) ? rest : undefined);
};

/**
 * The agent's steps, split into the tool calls and the commentary between them.
 *
 * The agent publishes the lead's own tool and text events, so its steps arrive
 * in the lead's step shape and can be fed straight to the lead's own timeline.
 */
const agentTimelineInput = (
  tool: Pick<ToolCall, 'children'> | undefined,
): { steps: ThinkingStep[]; insertions: TimelineTextInsertion[] } => {
  const steps: ThinkingStep[] = [];
  const insertions: TimelineTextInsertion[] = [];
  for (const child of Array.isArray(tool?.children) ? tool.children : []) {
    const data = (child?.data ?? {}) as Record<string, unknown>;
    if (child?.type === 'text') {
      const text = typeof data.text === 'string' ? data.text.trim() : '';
      // An insertion lands before the step it indexes, so the count so far
      // places this commentary after everything the agent had already done.
      if (text) insertions.push({ afterStepIndex: steps.length, text });
      continue;
    }
    if (child?.type !== 'tool') continue;
    if (!(data as unknown as ToolCall).function?.name) continue;
    steps.push({ type: 'tool', round_num: child.round_num, data: data as unknown as ToolCall });
  }
  return { steps, insertions };
};

export const getResearchAgentToolCalls = (
  tool: Pick<ToolCall, 'children'> | undefined,
): ToolCall[] => agentTimelineInput(tool).steps.map((step) => step.data as ToolCall);

/**
 * What the agent did, grouped exactly as the lead's own work is grouped.
 *
 * Runs of one tool family collapse to a single row and commentary breaks them
 * apart, which is what keeps a lead turn to a handful of lines. Listing every
 * call instead made an agent that searched five times look busier than a lead
 * that did the same thing, purely because nothing grouped it.
 */
export type AgentActivityRow =
  | { kind: 'commentary'; id: string; text: string }
  | { kind: 'tools'; id: string; steps: ThinkingStep[] };

export const getResearchAgentActivity = (
  tool: Pick<ToolCall, 'children'> | undefined,
): AgentActivityRow[] => {
  const { steps, insertions } = agentTimelineInput(tool);
  return interleaveThinkingAndText(steps, insertions).flatMap(
    (item, index): AgentActivityRow[] => {
      if (item.kind === 'text') {
        return [{ kind: 'commentary', id: `commentary-${index}`, text: item.text }];
      }
      if (item.kind === 'activity') {
        return [{ kind: 'tools', id: `tools-${index}`, steps: item.steps }];
      }
      return [];
    },
  );
};

export const liveResearchAgentStep = (
  tool: Pick<ToolCall, 'displayInfo' | 'children'> | undefined,
): string | undefined => {
  const tools = getResearchAgentToolCalls(tool);
  const lastTool = tools[tools.length - 1];
  const info = lastTool?.status === 'pending' || lastTool?.status === 'in_progress'
    ? (lastTool.displayInfo || '').trim()
    : '';
  if (info) return info;
  return currentResearchAgentStep(tool?.displayInfo);
};

export const researchAgentInFlight = (
  tool: Pick<ToolCall, 'function' | 'status'> | undefined,
  isLive = false,
): boolean => {
  if (!tool || toolCallFailed(tool) || tool.status === 'stopped') return false;
  const status = tool.status;
  if (status === 'pending' || status === 'in_progress') return true;
  // spawn_research completes with { status: "running" }. That is in-flight
  // only while this turn is still live; a stopped or historical chip is not.
  return isLive && researchOutputIsRunning(parseJsonObject(tool.function?.output));
};

export const getResearchAgentHeaderParts = (
  tool: Pick<ToolCall, 'function' | 'displayInfo' | 'children' | 'status' | 'objective'> | undefined,
  count: number,
  isLive = false,
): { title: string; status?: string } => {
  const failed = toolCallFailed(tool);
  const inProgress = researchAgentInFlight(tool, isLive);
  const plural = count > 1;
  const kind = specialistKindLabel(tool?.function?.name);
  const kindPlural = specialistKindPlural(tool?.function?.name);
  if (failed) {
    return { title: plural ? `${kindPlural} failed` : `${kind} failed` };
  }
  if (!inProgress) {
    const topic = tool ? getResearchAgentTopic(tool) : '';
    if (plural) {
      return { title: `${count} ${kindPlural} complete` };
    }
    if (topic) {
      return { title: agentTitleForTopic(topic) };
    }
    return { title: `${kind} complete` };
  }

  if (plural) {
    return { title: `${count} ${kindPlural} ${AGENT_WORKING_STATUS}...` };
  }
  // The live step is the status; "working" covers the gap before the agent
  // has reported what it is doing.
  const step = liveResearchAgentStep(tool) ?? AGENT_WORKING_STATUS;
  const topic = tool ? getResearchAgentTopic(tool) : '';
  if (topic) {
    return { title: agentTitleForTopic(topic), status: step };
  }
  return { title: `${kind} ${AGENT_WORKING_STATUS}...`, status: liveResearchAgentStep(tool) };
};

export const getResearchAgentHeaderLabel = (
  count: number,
  status?: ToolCall['status'],
  isLive = false,
  displayInfo?: string,
  toolName = 'spawn_research',
): string => {
  const parts = getResearchAgentHeaderParts(
    {
      status,
      displayInfo,
      function: { name: toolName, arguments: '' },
    },
    count,
    isLive,
  );
  return parts.status ? `${parts.title}  ${parts.status}` : parts.title;
};

export const getResearchAgentStepLabel = (tool: ToolCall, isLive = false): string => {
  const objective = getResearchAgentObjective(tool);
  const clipped = objective ? clipResearchAgentText(objective) : '';
  const failed = toolCallFailed(tool);
  const kind = specialistKindLabel(tool.function?.name);
  if (failed) {
    return clipped ? `Failed: ${clipped}` : `${kind} failed`;
  }
  if (researchAgentInFlight(tool, isLive)) {
    return clipped || `${kind} ${AGENT_WORKING_STATUS}...`;
  }
  return clipped || `${kind} complete`;
};

